/**
 * Cancel page - shown when user cancels Stripe checkout
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircleIcon, ArrowLeftIcon, HeartIcon } from '@heroicons/react/24/solid';

export function CancelPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isServicePayment = searchParams.get('purpose') === '2';

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Cancel Message Card */}
        <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg p-8 text-center space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="bg-gray-100 rounded-full p-4">
              <XCircleIcon className="h-16 w-16 text-gray-400" />
            </div>
          </div>

          {/* Heading */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Payment Cancelled</h1>
            <p className="text-lg text-gray-600">
              Your {isServicePayment ? 'service payment' : 'donation'} process was not completed.
            </p>
          </div>

          {/* Explanation */}
          <div className="bg-gray-50 rounded-lg p-6 text-left space-y-3">
            <h3 className="font-semibold text-gray-900 text-center">What happened?</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-1">•</span>
                <span>You cancelled the payment before it was completed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-1">•</span>
                <span>No charges were made to your payment method</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-1">•</span>
                <span>Your cart items are still saved if you'd like to try again</span>
              </li>
            </ul>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <span className="font-semibold">Tip:</span> If you experienced any issues during
              checkout, please try again or contact us for assistance.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 space-y-3">
            <button
              onClick={() => navigate('/checkout')}
              className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Return to Checkout
            </button>

            <button
              onClick={() => navigate(isServicePayment ? '/services' : '/donate')}
              className="w-full py-3 px-4 border-2 border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
            >
              <HeartIcon className="h-5 w-5" />
              {isServicePayment ? 'Continue Shopping' : 'Continue Browsing Donations'}
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full py-2 px-4 text-gray-600 hover:text-gray-900 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-gray-600 text-sm">
          <p>
            Need help?{' '}
            <a href="mailto:support@example.com" className="text-primary-600 hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
